import { CompanyLifecycleStatus, CompanyLifecycleSubStatus } from './companyLifecycle'
import { DealCloseReason, DealEventType, DealNextStep, DealOutcome, DealStage } from './deal'
import type { IDealLostNotes } from './prospects'
import { UserRole, UserStatus, userRoleGroups } from './user'

/** The roles a CRM record can be owned by: every internal user. */
export const CRM_OWNER_ROLES: UserRole[] = [...userRoleGroups.INTERNAL]

/** One bulk edit (owner, revisit date) covers at most this many companies. */
export const MAX_COMPANY_BULK_UPDATE = 500

export interface ICrmOwnerRef {
  id: string
  name: string
  firstName: string
  lastName: string
  // The owner's resolved avatar — uploaded S3 photo, else the legacy `users.avatar` URL (null
  // when neither is on file); googleAvatarUrl is the last fallback. Both feed the shared Avatar
  // component, matching how the activity queue renders an assignee.
  avatar: string | null
  googleAvatarUrl: string | null
  /**
   * The owner's own CRM person record (person.user_id), so a surface showing them can link
   * through to their person page. Absent on list payloads, which render the owner as plain
   * avatar + name — resolving it costs a query per payload, and `undefined` reads the same as
   * "no link" at every call site.
   */
  personId?: string | null
}

export interface ICrmListResponse<T> {
  data: T[]
  total: number
}

export interface IFundProfile {
  size: number | null
  minDealSize: number | null
  maxDealSize: number | null
  minRevenue: number | null
  maxRevenue: number | null
}

// Investor/fund capital preferences — the organization_capital_info row joined on
// organization_capital_info.organization_id. Rendered as the Capital Profile card
// for investor-type orgs; null when the org has no capital-info row.
export interface ICapitalProfile {
  size: number | null
  minDealSize: number | null
  maxDealSize: number | null
  minRevenue: number | null
  maxRevenue: number | null
  minValuation: number | null
  maxValuation: number | null
  targetRounds: string[] | null
  preferredInstruments: string[] | null
  collateralPreference: string | null
  otherDealPreferences: string | null
  isCompetitor: boolean
  isExitPartner: boolean
}

// Portfolio-exposure totals for an organization's linked legacy entity, read from
// the precomputed OpenSearch entity index (same source as the Customer/Vendor
// summary tabs). All null when the org has no linked entity or no exposure.
export interface IEntityExposure {
  totalAr: number | null
  totalAp: number | null
  totalExposure: number | null
  dso: number | null
}

export interface IOrganizationRow {
  id: string
  source: 'organization' | 'entity'
  name: string | null
  type: string | null
  subType: string | null
  lastInteractionDate: string | null
  roles: string[]
  fundProfile: IFundProfile | null
  relatedCompanies: number
  // Total AR/AP/Exposure + Avg DSO from the linked entity (null for orgs with none).
  exposure: IEntityExposure | null
  entityInfoId: string | null
  owner: ICrmOwnerRef | null
  // Whether a logo is set — see IPersonRow.hasPhoto. Keyed under `organizations` or `entities`
  // depending on `source`.
  hasPhoto?: boolean
}

export interface ICompanyRow {
  id: string
  name: string | null
  // The Salesforce mirror (Account_Sub_Type__c / Lead.Status). Kept for callers
  // that still read it; the lifecycle pair below is what the CRM displays.
  status: string | null
  lifecycleStatus: CompanyLifecycleStatus | null
  lifecycleSubStatus: CompanyLifecycleSubStatus | null
  // Stage of the company's open deal; null when it has none.
  dealStage: DealStage | null
  /** How the latest deal closed, when the company has no open one. */
  latestDealOutcome: DealOutcome | null
  latestDealCloseReason: DealCloseReason | null
  latestDealCloseNote: string | null
  industryType: string | null
  industrySubtype: string | null
  lastInteractionDate: string | null
  /** Set while the company is Radar · Tracking. */
  revisitOn: string | null
  owner: ICrmOwnerRef | null
  hq: { city: string | null; state: string | null } | null
  commitment: number | null
  clientStatus: string | null
  peopleCount: number
  // Whether a logo is set — see IPersonRow.hasPhoto.
  hasPhoto?: boolean
}

/** One recorded move on a deal's timeline. */
export interface IDealEvent {
  id: string
  eventType: DealEventType
  fromStage: string | null
  toStage: string | null
  note: string | null
  createdAt: string
  actor: { id: string; firstName: string | null; lastName: string | null } | null
}

/** A deal's path through the stages, read from its event log. */
export interface IDealHistory {
  /** Every stage the deal reached, in ladder order. */
  stagesReached: DealStage[]
  peakStage: DealStage | null
  /** Moves to an earlier stage, such as Move to BD. */
  fallBacks: { from: DealStage; to: DealStage; at: string }[]
  /** Whole days spent in each stage, summed across visits. */
  daysInStage: Partial<Record<DealStage, number>>
}

/** A deal as the company page renders it, newest first. */
export interface IDealRow {
  id: string
  companyId: string | null
  name: string | null
  /** "Deal #3", with the year it opened. */
  sequence: number | null
  stage: DealStage | null
  outcome: DealOutcome | null
  closeReason: DealCloseReason | null
  closeNote: string | null
  /** Set only while the deal is Lost or Passed. */
  lostDealNotes: IDealLostNotes | null
  nextStep: DealNextStep | null
  closedAt: string | null
  isOpen: boolean
  commitmentAmount: number | null
  closeDate: string | null
  lastActivityDate: string | null
  salesforceId: string | null
  salesforceLeadId: string | null
  /** The deal's current prospect version (BD workspace), when there is one. */
  workspaceId: string | null
  /** That workspace's date, as the version picker shows it. */
  workspaceDate: string | null
  owner: ICrmOwnerRef | null
  events: IDealEvent[]
  history: IDealHistory
  createdAt: string
  /** When the deal began: Salesforce's creation date for a synced deal, else Atlas's. */
  openedAt: string
}

export interface IPersonRow {
  id: string
  firstName: string | null
  lastName: string | null
  fullName: string
  emailAddress: string | null
  phoneNumber: string | null
  lastInteractionDate: string | null
  primaryTitle: string | null
  primaryAffiliation: string | null
  primaryCompanyId: string | null
  primaryOrganizationId: string | null
  type: string
  owner: ICrmOwnerRef | null
  /**
   * Whether this person has an uploaded profile photo, so a list can render the photo only for
   * the rows that have one.
   *
   * A flag rather than the photo itself: an uploaded photo serializes as a base64 data URI, and a
   * page of rows carrying their own bytes is exactly what the avatar endpoint exists to avoid
   * (see services/photos/PhotoShow). The flag costs the same Redis-cached lookup the bytes would
   * and saves a request per photo-less row — most CRM contacts have no photo, so without it a
   * 50-row page fires 50 requests that all resolve to null.
   */
  hasPhoto: boolean
}

// Controlled role vocabulary for the editable company Team. Atlas-owned — a
// person can hold multiple roles at one company, held as a set on the single
// relationship row (person_org_company_relationship.role is text[]).
export enum CrmRelationshipRole {
  Contact = 'Contact',
  Executive = 'Executive',
  BoardMember = 'Board Member',
  Reference = 'Reference',
}

export const CRM_RELATIONSHIP_ROLES: CrmRelationshipRole[] = [
  CrmRelationshipRole.Contact,
  CrmRelationshipRole.Executive,
  CrmRelationshipRole.BoardMember,
  CrmRelationshipRole.Reference,
]

/**
 * Which `role_attributes` keys each role owns. `ICrmRoleAttributes` is a flat
 * bag, but every key belongs to exactly one role, so a writer that lands a
 * single role can replace that role's keys and leave the others alone.
 *
 * This exists because `role` is now a set on ONE relationship row: the intake
 * sync lands a person's roles one at a time (a founder who is both a board
 * member and a reference arrives as two entries), so a writer that replaced the
 * whole attribute bag would destroy the previous role's attributes — e.g. the
 * board member's `boardOutsideAffiliation`, which the Amendment Analysis Board
 * Members exhibit renders as its Company column. Under the old one-row-per-role
 * model each role carried its own bag and the question never came up.
 */
export const CRM_ROLE_ATTRIBUTE_KEYS: Record<
  CrmRelationshipRole,
  ReadonlyArray<keyof ICrmRoleAttributes>
> = {
  [CrmRelationshipRole.Contact]: [],
  [CrmRelationshipRole.Executive]: ['ownershipPercent'],
  [CrmRelationshipRole.BoardMember]: ['boardOutsideAffiliation'],
  [CrmRelationshipRole.Reference]: ['referenceBusiness', 'referenceRelationship'],
}

// A relationship's roles reach the client two ways: as the set itself
// (ICrmRelatedPerson.role) on the detail payloads, and as one joined label on
// the network rails, whose rows stay scalar so the shared IFilter config can
// substring-filter and sort them (see IAffiliationGroupRow.role). Both
// directions live here so the separator can't drift between the two.
export const CRM_ROLE_LABEL_SEPARATOR = ', '

/**
 * Trimmed, non-empty, de-duplicated roles — the only safe way to read a role
 * set. It is a SET: a multi-select that submits the same role twice, or two
 * merged rows that both carried "Contact", must not store it twice.
 */
export const roleSet = (roles: string[] | null | undefined): string[] => [
  ...new Set((roles ?? []).map((role) => role.trim()).filter(Boolean)),
]

/** The role set as the single label the CRM rails render, filter and sort on. */
export const roleSetToLabel = (roles: string[] | null | undefined): string | null =>
  roleSet(roles).join(CRM_ROLE_LABEL_SEPARATOR) || null

/**
 * Inverse of `roleSetToLabel`, for a caller that only holds the joined label.
 * Named to read in the same direction as its counterpart: label -> set.
 *
 * LOSSY BY CONSTRUCTION, because the separator is also legal inside a role. A
 * free-text role carrying ', ' — `Lead.Title` reaches `role` via `toArray`,
 * which splits only on ';', so 'VP, Sales' survives as one element — comes back
 * as two roles. There is no separator that can't appear in free text, so the
 * only real fix is not to round-trip through a label; prefer passing the set
 * itself (ICrmRelatedPerson.role is text[]) wherever you can. This is safe on
 * the company Team rail because `atlasRoles` narrows to the controlled
 * vocabulary, which has no commas, and a Salesforce-managed row never submits
 * its roles at all.
 */
export const roleLabelToSet = (label: string | null | undefined): string[] =>
  roleSet(label ? label.split(CRM_ROLE_LABEL_SEPARATOR) : [])

// Controlled vocabulary for the role an organization plays for a company — the
// editable company Organizations rail. Curated to the third-party firms the UW
// application captures (law firm / outsourced CFO / accounting firm / 3PL, see
// ClientEntityRelationshipType) plus the common Salesforce-synced relationship
// types. Salesforce-synced rows may carry ANY free-text Vendor_Relationship__c
// Type__c value — those keep displaying as-is; this list only bounds what an
// internal user can pick.
// Distinct from SalesforceAccountRecordType, which shares three of these names but
// describes `organization.record_type` (the Account RecordType) rather than the
// relationship type in `organization.type`.
export enum CrmOrganizationRelationshipType {
  Attorney = 'Attorney',
  OutsourcedCFO = 'Outsourced CFO',
  Accountant = 'Accountant',
  Distribution3PL = '3PL/Fulfillment',
  InsuranceBroker = 'Insurance Broker',
  CurrentLender = 'Current Lender',
  Investor = 'Investor',
  ReferralSource = 'Referral Source',
  Vendor = 'Vendor',
}

export const CRM_ORGANIZATION_RELATIONSHIP_TYPES: CrmOrganizationRelationshipType[] = [
  CrmOrganizationRelationshipType.Attorney,
  CrmOrganizationRelationshipType.OutsourcedCFO,
  CrmOrganizationRelationshipType.Accountant,
  CrmOrganizationRelationshipType.Distribution3PL,
  CrmOrganizationRelationshipType.InsuranceBroker,
  CrmOrganizationRelationshipType.CurrentLender,
  CrmOrganizationRelationshipType.Investor,
  CrmOrganizationRelationshipType.ReferralSource,
  CrmOrganizationRelationshipType.Vendor,
]

// The category of an organization_company_relationship row: which Salesforce object it comes
// from, or for an Atlas-created row, what its type implies. `type` keeps the
// finer-grained role (Broker, Attorney, …).
export enum OrganizationCompanyRelationshipKind {
  Vendor = 'vendor',
  Investor = 'investor',
  CurrentLender = 'current_lender',
}

export const relationshipKindForType = (
  type: string | null,
): OrganizationCompanyRelationshipKind => {
  if (type === CrmOrganizationRelationshipType.Investor)
    return OrganizationCompanyRelationshipKind.Investor
  if (type === CrmOrganizationRelationshipType.CurrentLender) {
    return OrganizationCompanyRelationshipKind.CurrentLender
  }
  return OrganizationCompanyRelationshipKind.Vendor
}

// Doubles as the row's status — there is no separate status column, which is why
// `Planned` is a value here.
export enum CapitalRaiseRoundType {
  Planned = 'Planned',
  PreSeed = 'Pre-Seed',
  Seed = 'Seed',
  SeriesA = 'Series A',
  SeriesB = 'Series B',
  SeriesC = 'Series C',
  SeriesD = 'Series D',
  SeriesE = 'Series E',
  SeriesF = 'Series F',
  Bridge = 'Bridge',
  Safe = 'SAFE',
  ConvertibleNote = 'Convertible Note',
  VentureDebt = 'Venture Debt',
  Other = 'Other',
}

/**
 * Round types newest-first, for ordering raises that carry no date — which every
 * cap-table-derived row does, since a cap table states ownership as of its record
 * date and never when a round closed.
 *
 * A Series B came after a Series A, which came after Seed, so a later stage sorts
 * higher. `Planned` leads because it has not happened yet. The four instruments have
 * no reliable slot — a bridge or SAFE can land anywhere between priced rounds — so
 * rather than fake a position they sit below the priced rounds, with `Other` last.
 */
export const CAPITAL_RAISE_ROUND_SENIORITY: CapitalRaiseRoundType[] = [
  CapitalRaiseRoundType.Planned,
  CapitalRaiseRoundType.SeriesF,
  CapitalRaiseRoundType.SeriesE,
  CapitalRaiseRoundType.SeriesD,
  CapitalRaiseRoundType.SeriesC,
  CapitalRaiseRoundType.SeriesB,
  CapitalRaiseRoundType.SeriesA,
  CapitalRaiseRoundType.Seed,
  CapitalRaiseRoundType.PreSeed,
  CapitalRaiseRoundType.Bridge,
  CapitalRaiseRoundType.Safe,
  CapitalRaiseRoundType.ConvertibleNote,
  CapitalRaiseRoundType.VentureDebt,
  CapitalRaiseRoundType.Other,
]

/** Rank for sorting; an unrecognised value sorts after everything known. */
export const capitalRaiseRoundRank = (roundType: string | null | undefined): number => {
  const index = CAPITAL_RAISE_ROUND_SENIORITY.findIndex(
    (type) => String(type) === (roundType ?? ''),
  )
  return index === -1 ? CAPITAL_RAISE_ROUND_SENIORITY.length : index
}

export const CAPITAL_RAISE_ROUND_TYPES: CapitalRaiseRoundType[] = [
  CapitalRaiseRoundType.Planned,
  CapitalRaiseRoundType.PreSeed,
  CapitalRaiseRoundType.Seed,
  CapitalRaiseRoundType.SeriesA,
  CapitalRaiseRoundType.SeriesB,
  CapitalRaiseRoundType.SeriesC,
  CapitalRaiseRoundType.SeriesD,
  CapitalRaiseRoundType.SeriesE,
  CapitalRaiseRoundType.SeriesF,
  CapitalRaiseRoundType.Bridge,
  CapitalRaiseRoundType.Safe,
  CapitalRaiseRoundType.ConvertibleNote,
  CapitalRaiseRoundType.VentureDebt,
  CapitalRaiseRoundType.Other,
]

// Role-specific attributes stored on the relationship (not the person). All
// optional — only the keys relevant to the row's role are populated.
export interface ICrmRoleAttributes {
  // Executive
  ownershipPercent?: number | null
  // Board Member
  boardOutsideAffiliation?: string | null
  // Reference
  referenceBusiness?: string | null
  referenceRelationship?: string | null
  // Board Member — LCR exhibit fields. `boardRelation` is the LCR "Relation to
  // company" (one of BOARD_MEMBER_RELATIONS); `lcrSource: 'manual'` marks a
  // relationship a reviewer added on the Loan Committee Report (so it can be told
  // apart from application-synced ones), with `lcrEditedById`/`lcrEditedAt`
  // recording the last LCR edit (the relationship has no updated_by column).
  boardRelation?: string | null
  lcrSource?: 'manual' | null
  lcrEditedById?: string | null
  lcrEditedAt?: string | null
}

export interface ICrmRelatedPerson {
  id: string | null
  // person_org_company_relationship.id — the editable relationship row.
  relationshipId: string | null
  name: string
  emailAddress: string | null
  // person_org_company_relationship.role is text[] — one person can be several
  // things at one company at once (an investor who also sits on the board), so
  // this is the whole set. Test it with membership, never equality, and join it
  // for display.
  role: string[] | null
  isActive: boolean | null
  isPrimaryContactForCompany: boolean | null
  roleAttributes: ICrmRoleAttributes | null
  // person.last_interaction_date (Salesforce-synced, often null) — the "Last touch"
  // caption on the People panel.
  lastInteractionDate: string | null
}

export interface ICrmRelatedCompanyOrOrg {
  // The other party's id; `kind` says whether it's a company or an organization.
  id: string | null
  kind: 'company' | 'organization' | null
  name: string | null
  type: string | null
  isActive: boolean | null
  // organization_company_relationship.last_seen — the "Last seen" caption on the
  // Related Companies panel.
  lastSeen: string | null
}

export interface IPersonAffiliation {
  id: string
  // The relationship's full role set — see ICrmRelatedPerson.role.
  role: string[] | null
  isActive: boolean | null
  isDirect: boolean | null
  startDate: string | null
  endDate: string | null
  company: { id: string; name: string | null } | null
  organization: { id: string; name: string | null } | null
}

export interface IEntityRoleFlags {
  debtor: boolean
  creditor: boolean
  investor: boolean
  lender: boolean
  acquirer: boolean
}

/** The organization fields Salesforce restricts to a picklist. */
export type OrganizationPicklistField =
  | 'type'
  | 'subType'
  | 'industryFocus'
  | 'rating'
  | 'targetRounds'
  | 'preferredInstruments'
  | 'collateralPreference'

/**
 * Dropdown values for an organization's picklist fields, narrowed to its Salesforce record type.
 * A field missing from `options` is free text. Sub-type depends on type, so it is keyed by type.
 */
export interface IOrganizationFieldOptions {
  options: Partial<Record<OrganizationPicklistField, string[]>>
  subTypeByType: Record<string, string[]>
}

export interface IOrganizationDetail {
  id: string
  source: 'organization' | 'entity'
  name: string | null
  // The organization's logo. Keyed under `organizations` or `entities` depending on `source`.
  photo: string | null
  type: string | null
  subType: string | null
  website: string | null
  phone: string | null
  description: string | null
  // Set when the record mirrors a Salesforce record; inline edits are written to it too.
  salesforceId: string | null
  // organization.record_type — e.g. "Referral Source"; null for entity-sourced pages.
  recordType: string | null
  // organization.industry_focus (text[] column) — rendered joined with ", ".
  industryFocus: string[] | null
  // organization.rating — e.g. "A — active referrer".
  rating: string | null
  roles: string[]
  owner: ICrmOwnerRef | null
  lastInteractionDate: string | null
  entityInfoId: string | null
  // Financial role flags from the linked entity (null when no entity is linked).
  entityFlags: IEntityRoleFlags | null
  // Investor capital preferences (Capital Profile card); null for non-investor orgs.
  capitalInfo: ICapitalProfile | null
  // AI dossier text + when it was last generated (organization.ai_dossier /
  // ai_dossier_generated_at). On-demand only — never auto-generated on load.
  aiDossier: string | null
  aiDossierGeneratedAt: string | null
  people: ICrmRelatedPerson[]
  companies: ICrmRelatedCompanyOrOrg[]
}

export interface ICompanyDetail {
  id: string
  name: string | null
  // The company's logo. A detail payload carries the image itself; see IPersonRow.hasPhoto for why
  // list rows carry only the flag.
  photo: string | null
  // The Salesforce mirror; the derived lifecycle below is what the page shows.
  status: string | null
  lifecycleStatus: CompanyLifecycleStatus | null
  lifecycleSubStatus: CompanyLifecycleSubStatus | null
  industryType: string | null
  industrySubtype: string | null
  website: string | null
  dealType: string | null
  // Salesforce Account id — used to kick off the client-intake / createProspect
  // flow for a company that has no OPS reporting yet.
  salesforceId: string | null
  owner: ICrmOwnerRef | null
  // Atlas-owned editable fields (not Salesforce-synced). company.federal_tax_id is
  // intentionally absent — it's stored on the company but never projected to the
  // client; the submitted-application form is the only place the tax ID is shown.
  legalCompanyName: string | null
  stateOfFormation: string | null
  clientInfo: {
    id: string
    clientStatus: string | null
    maxLineAmount: number | null
    // OpsReporting.id for the prospect profile page (null unless the client is a prospect).
    firstOPSReportingId: string | null
  } | null
  people: ICrmRelatedPerson[]
  organizations: ICrmRelatedCompanyOrOrg[]
  social: ICompanySocial
  // No client record and no deal, deleted ones included: CompanyDelete would succeed.
  isCompanyDeletable: boolean
}

/**
 * The company's social presence from Atlas's daily social tracking — read-only. Each field is the
 * latest value on file across the prospect- and client-side records, so converting to a client
 * doesn't lose what the prospect rows held. Null when nothing was ever tracked.
 */
export interface ICompanySocial {
  instagram: string | null
  instagramFollowers: number | null
  linkedin: string | null
}

// The linked Atlas account (legacy `users` row) for a person, when person.user_id
// is set. Curated subset — never the full user record — surfaced in the person
// profile's "Atlas Account" section.
export interface IPersonLinkedUser {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  role: UserRole
  status: UserStatus
  isInvited: boolean
}

export interface IPersonDetail {
  id: string
  fullName: string
  emailAddress: string | null
  phoneNumber: string | null
  mobilePhone: string | null
  linkedinProfileUrl: string | null
  // Set when the person mirrors a Salesforce Contact/Lead, whose nightly sync can overwrite inline edits.
  salesforceId: string | null
  primaryTitle: string | null
  primaryAffiliation: string | null
  // The affiliation row behind primaryAffiliation — the one the Company row's picker shows as chosen.
  primaryAffiliationId: string | null
  primaryCompanyId: string | null
  primaryOrganizationId: string | null
  type: string
  owner: ICrmOwnerRef | null
  affiliations: IPersonAffiliation[]
  // Quick-capture notes edited inline on the person profile.
  personalDetails: string | null
  professionalDetails: string | null
  // Linked Atlas account (legacy user), null when the person has no user_id.
  userId: string | null
  linkedUser: IPersonLinkedUser | null
  // City/state from the person's Contact-sourced address first, else the newest one that names
  // either; null when no address on file does.
  location: { city: string | null; state: string | null } | null
  // The profile page renders exactly one face, so the detail payload carries the photo itself
  // rather than costing a second round trip. List payloads carry only IPersonRow.hasPhoto.
  photo: string | null
}
